'use client';

import React from "react";
import { FaDownload } from "react-icons/fa6";

// Served from /public so they resolve to plain URLs (works in native <img>/url()).
const logo_space = "/images/branding/logoSpace.png";
const logo_size = "/images/branding/logoSize.png";
const greenBg = "/images/branding/green-bg.png";

const logo = "/images/logo.svg";
const logoDark = "/images/logo-dark.svg";
const logoLight = "/images/logo-light.svg";

const LogoSection = () => {
  const triggerDownload = (href, filename) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // SVG download: fetch the file as a blob so the browser saves it instead of
  // navigating to it (a same-origin <a download> to an .svg can open inline).
  const handleDownloadSVG = async (src, label) => {
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      triggerDownload(url, `${label}.svg`);
      URL.revokeObjectURL(url);
    } catch {
      triggerDownload(src, `${label}.svg`);
    }
  };

  // PNG download: raster sources download directly; SVG sources are rasterised
  // to a high-resolution PNG on a canvas first.
  const handleDownloadPNG = async (variant) => {
    if (variant.pngSrc) {
      triggerDownload(variant.pngSrc, `${variant.label}.png`);
      return;
    }
    try {
      const res = await fetch(variant.svgSrc);
      const svgText = await res.text();
      const svgBlob = new Blob([svgText], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);
      const img = new window.Image();
      img.onload = () => {
        const scale = 4; // export at 4x for crisp PNGs
        const w = img.naturalWidth || 512;
        const h = img.naturalHeight || 512;
        const canvas = document.createElement("canvas");
        canvas.width = w * scale;
        canvas.height = h * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const pngUrl = URL.createObjectURL(blob);
          triggerDownload(pngUrl, `${variant.label}.png`);
          URL.revokeObjectURL(pngUrl);
        }, "image/png");
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        triggerDownload(variant.svgSrc, `${variant.label}.svg`);
      };
      img.src = url;
    } catch {
      triggerDownload(variant.svgSrc, `${variant.label}.svg`);
    }
  };

  return (
    <>
      <section className="my-12">
        <h2 className="text-2xl sm:text-3xl xl:text-4xl text-[--accent-light] my-4">
          Logo Usage
        </h2>

        <div className="flex flex-wrap justify-center sm:justify-between items-end gap-8 mx-6 my-10">
          {[
            {
              label: "Default",
              src: logo,
              svgSrc: logo,
              imgClass: "h-20 sm:h-24 object-contain",
              containerClass: "bg-black px-5 py-4",
            },
            {
              label: "Profile",
              src: greenBg,
              pngSrc: greenBg,
              imgClass: "h-28 sm:h-32 object-contain",
              containerClass: "",
            },
            {
              label: "Dark Mode",
              src: logoDark,
              svgSrc: logoDark,
              imgClass: "h-20 sm:h-24 w-56 object-contain",
              containerClass: "bg-black px-6 py-4",
            },
            {
              label: "Light Mode",
              src: logoLight,
              svgSrc: logoLight,
              imgClass: "h-20 sm:h-24 w-56 object-contain",
              containerClass: "bg-white px-6 py-4",
            },
          ].map((variant, index) => (
            <div key={index} className="group">
              <div
                className={`rounded-lg overflow-hidden border border-color-95 ${variant.containerClass}`}
              >
                <img
                  loading="lazy"
                  className={variant.imgClass}
                  alt={variant.label}
                  src={variant.src}
                />
              </div>
              <div className="p-2">
                <p className="text-lg mb-2">{variant.label}</p>
                <div className="flex items-center gap-2">
                  {variant.svgSrc && (
                    <button
                      type="button"
                      onClick={() =>
                        handleDownloadSVG(variant.svgSrc, variant.label)
                      }
                      className="flex items-center gap-1.5 rounded-md border border-color-95 px-2.5 py-1 text-xs hover:text-[var(--accent-light)] hover:border-[var(--accent-light)] transition-colors cursor-pointer"
                      title={`Download ${variant.label} as SVG`}
                    >
                      <FaDownload size={11} /> SVG
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDownloadPNG(variant)}
                    className="flex items-center gap-1.5 rounded-md border border-color-95 px-2.5 py-1 text-xs hover:text-[var(--accent-light)] hover:border-[var(--accent-light)] transition-colors cursor-pointer"
                    title={`Download ${variant.label} as PNG`}
                  >
                    <FaDownload size={11} /> PNG
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-between mx-6 mb-10">
          <div className="w-[45%]">
            <h3 className="text-xl font-bold mb-2">Clear Space</h3>
            <p className="text-color-2">
              Maintain a minimum clearance of <br /> 20px around the logo to
              ensure it stands out.
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <img
                src={logo_space}
                alt="Clear Space Example"
                className="w-40 h-40"
              />
            </div>
          </div>

          <div className="w-[45%]">
            <h3 className="text-xl font-bold mb-2">Minimum size</h3>
            <p className="text-color-2">
              Minimum print size .75” wide.
              <br />
              Minimum digital size 60 pixels wide.
            </p>
            <div className="flex justify-center gap-6 mt-8">
              <img
                src={logo_size}
                alt="Minimum Size Example"
                className="w-40 h-40 rounded-md"
              />
            </div>
          </div>
        </div>

        <div className="mx-6 mb-10">
          <h3 className="text-xl font-bold mb-2">Visibility</h3>
          <p className="text-color-2">
            When placing a logo on a background, aim for a contrast ratio of
            2.25:1 or higher to ensure visibility. If the contrast is too low,
            adjust the background.
          </p>

          <div className="grid grid-cols-3 gap-6 mt-6">
            {[
              // Correct items
              {
                text: "Perfect Contrast",
                ratio: "4.8:1",
                check: true,
                bgColor: "bg-black",
                textColor: "text-gray-300",
              },
              {
                text: "Strong Clarity",
                ratio: "4.2:1",
                check: true,
                bgColor: "bg-zinc-900/70",
                textColor: "text-gray-300",
              },
              {
                text: "Good Visibility",
                ratio: "3.8:1",
                check: true,
                bgColor: "bg-zinc-900",
                textColor: "text-gray-200",
              },

              // Incorrect items
              {
                text: "Low Readability",
                ratio: "1.7:1",
                check: false,
                bgColor: "bg-green-900",
                textColor: "text-gray-200",
              },
              {
                text: "Barely Visible",
                ratio: "1.5:1",
                check: false,
                bgColor: "bg-green-700",
                textColor: "text-gray-300",
              },
              {
                text: "Very Poor",
                ratio: "1.2:1",
                check: false,
                bgColor: "bg-green-400",
                textColor: "text-gray-700",
              },
            ].map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg text-center border border-color-95 ${item.bgColor} ${item.textColor}`}
              >
                <img
                  loading="lazy"
                  className="w-40 sm:w-60 mx-auto my-3"
                  alt="brand_logo"
                  src={logoDark}
                />
                <p className="font-semibold text-sm sm:text-base">
                  {item.text}
                </p>
                <p className="text-xs sm:text-sm">
                  Contrast ratio = {item.ratio}
                </p>
                <span
                  className={item.check ? "text-green-500" : "text-red-500"}
                >
                  {item.check ? "✔" : "✖"}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mx-6 mb-10">
          <h2 className="text-xl font-bold mb-2">
            Incorrect Usage & Things To Avoid
          </h2>
          <p className="text-base md:text-color-2 mb-6">
            Using our logo consistently ensures brand recognition and allows for
            creativity elsewhere. Avoid these common mistakes to maintain the
            integrity of our brand identity.
          </p>

          {/* Grid container for incorrect usage examples */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 md:gap-10">
            {[
              {
                text: "Don't stretch or distort the logo",
                imgClass: "w-full h-full scale-x-75",
                containerClass: "overflow-hidden",
              },
              {
                text: "Avoid placing the logo on cluttered backgrounds",
                imgClass: "w-24 opacity-75",
                containerClass:
                  " bg-gradient-to-br from-purple-800 via-green-800 to-pink-800 flex items-center justify-center",
              },
              {
                text: "Don't crop or cut off the logo",
                imgClass: "object-cover scale-150",
                containerClass: "overflow-hidden",
              },
              {
                text: "Don't use on low contrast backgrounds",
                imgClass: "w-20 sm:w-24 opacity-50",
                containerClass: "bg-gray-200 flex items-center justify-center",
              },
              {
                text: "Don’t rotate",
                imgClass: "object-cover w-20 sm:w-24 h-auto rotate-45",
                containerClass: "",
              },
              {
                text: "Don’t change the color",
                imgClass:
                  "object-cover w-20 sm:w-24 h-auto filter sepia hue-rotate-[190deg] saturate-[500%]",
                containerClass: "",
              },
              {
                text: "Don’t add effects",
                imgClass: "object-cover opacity-80",
                containerClass:
                  "shadow-3xl shadow-inner shadow-blue-500 bg-gradient-to-tr from-transparent via-blue-800 to-transparent",
              },
              {
                text: "Don’t use as a pattern",
                imgClass: "opacity-30",
                containerClass: "bg-repeat relative overflow-hidden",
                customStyle: {
                  backgroundImage: `url(${logo})`,
                  backgroundSize: "50px",
                },
                badge: "New",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center"
              >
                <div
                  className={`${item.containerClass} rounded-md mx-auto relative p-4`}
                  style={item.customStyle}
                >
                  {item.badge && (
                    <div className="absolute top-2 -left-8 bg-red-500 text-white text-xs font-bold px-8 py-1 rotate-[-45deg] shadow-lg">
                      {item.badge}
                    </div>
                  )}
                  <img src={logo} alt={item.text} className={item.imgClass} />
                </div>
                <p className="mt-2 text-color-2 text-sm sm:text-base">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default LogoSection;
